import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { en } from '../locales/en';
import { es } from '../locales/es';
import type { Messages } from '../locales/messages';

export type Lang = 'en' | 'es';

const DICTIONARIES: Record<Lang, Messages> = { en, es };

type Path<T, Prefix extends string = ''> = T extends string
  ? Prefix
  : {
      [K in keyof T & string]: Path<T[K], Prefix extends '' ? K : `${Prefix}.${K}`>;
    }[keyof T & string];

export type MessageKey = Path<Messages>;

/** Shared type for the `t` function so components can accept it as a prop without
 * TypeScript widening it to `(key: string) => string` (which would reject the
 * literal-union MessageKey type useI18n's t actually has). */
export type TFunction = (key: MessageKey, vars?: Record<string, string | number>) => string;

function getByPath(obj: unknown, path: string): string {
  const value = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
  return typeof value === 'string' ? value : path;
}

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => String(vars[key] ?? ''));
}

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({
  children,
  storageKey,
  defaultLang,
}: {
  children: ReactNode;
  storageKey: string;
  defaultLang: Lang;
}) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === 'undefined') return defaultLang;
    const stored = window.localStorage.getItem(storageKey);
    return stored === 'en' || stored === 'es' ? stored : defaultLang;
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback(
    (next: Lang) => {
      setLangState(next);
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Private browsing / storage disabled — language just won't persist across visits.
      }
    },
    [storageKey],
  );

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>) =>
      interpolate(getByPath(DICTIONARIES[lang], key), vars),
    [lang],
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider');
  return ctx;
}
